/**
 * Schema-driven column mapping of the list upload page (KAN-727, moved out of
 * DatasetUploadPage).
 *
 * One row per schema field. When the file / sheet headers are known each row
 * is a select of those headers ("не загружать" for optional fields) with a
 * sample value; otherwise a free-text input for the column name. A server
 * "column not found" error is shown under its field.
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import type { ColumnMapping, SchemaField, UploadHeaders } from '@/types/dataset';

export const SKIP_COLUMN_LABEL = 'не загружать';

interface ColumnMappingFieldsProps {
  schema: SchemaField[];
  mapping: ColumnMapping;
  onChange: (fieldName: string, column: string) => void;
  headers?: UploadHeaders;
  headersError?: string | null;
  headersLoading?: boolean;
  fieldErrors?: Record<string, string>;
}

function sampleOf(headers: UploadHeaders | undefined, column: string): string {
  if (!headers || !column) return '';
  const values = headers.samples
    .map((row) => (row[column] ?? '').trim())
    .filter(Boolean);
  return values.join(', ');
}

export function ColumnMappingFields({
  schema,
  mapping,
  onChange,
  headers,
  headersError,
  headersLoading,
  fieldErrors = {},
}: ColumnMappingFieldsProps): JSX.Element {
  const columns = headers?.headers ?? [];
  const hasHeaders = columns.length > 0;
  const options = columns.map((h) => ({ value: h, label: h }));

  return (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <div>
        <h3 className="text-base font-medium text-gray-900">Сопоставление колонок</h3>
        <p className="text-sm text-gray-500">
          {hasHeaders
            ? 'Выберите колонку таблицы для каждого поля.'
            : 'Укажите название колонки в файле для каждого поля.'}
        </p>
        {headersLoading && (
          <p data-testid="upload-headers-loading" className="text-xs text-gray-400">
            Читаем заголовки колонок...
          </p>
        )}
        {headersError && (
          <p data-testid="upload-headers-error" className="text-xs text-amber-700">
            Не удалось прочитать заголовки: {headersError}. Введите названия колонок вручную.
          </p>
        )}
      </div>
      {schema.map((field) => {
        const id = `mapping-input-${field.name}`;
        const value = mapping[field.name] ?? '';
        const sample = hasHeaders ? sampleOf(headers, value) : '';
        const fieldError = fieldErrors[field.name];
        return (
          <div
            key={field.name}
            data-testid={`mapping-row-${field.name}`}
            className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start"
          >
            <Label htmlFor={id} className="sm:pt-2">
              {field.label || field.name}
              {field.required && <span className="text-red-500"> *</span>}
            </Label>
            <div className="space-y-1">
              {hasHeaders ? (
                <NativeSelect
                  id={id}
                  data-testid={`mapping-select-${field.name}`}
                  value={value}
                  onChange={(e) => onChange(field.name, e.target.value)}
                  placeholder={field.required ? 'Выберите колонку' : undefined}
                  options={
                    field.required
                      ? options
                      : [{ value: '', label: SKIP_COLUMN_LABEL }, ...options]
                  }
                />
              ) : (
                <Input
                  id={id}
                  data-testid={id}
                  placeholder="Название колонки в файле"
                  value={value}
                  onChange={(e) => onChange(field.name, e.target.value)}
                />
              )}
              {sample && (
                <p data-testid={`mapping-sample-${field.name}`} className="text-xs text-gray-400 truncate">
                  Например: {sample}
                </p>
              )}
              {fieldError && (
                <p data-testid={`mapping-error-${field.name}`} className="text-xs text-red-600">
                  {fieldError}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
